import React, { useEffect, useMemo, useState } from 'react';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import classNames from 'classnames';

import { LanguageIsoCode } from 'utils/enums';
import TextEditor from 'components/TextEditor';
import DragItem from 'components/DragAndDrop/DragItem';

import classes from './AccordionItem.module.scss';

const getInitialEditorState = (initialRichText) => {
  if (initialRichText) {
    const blocksFromHtml = htmlToDraft(initialRichText);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );
    return EditorState.createWithContent(contentState);
  }

  return EditorState.createEmpty();
};

const AccordionItem = ({
  item,
  index,
  selectedLanguage,
  onTitleChange,
  onBodyChange,
  onToggleOpen,
}) => {
  const [editorState, setEditorState] = useState(
    getInitialEditorState(
      item.body?.richText?.[selectedLanguage] ||
        item.body?.richText?.[LanguageIsoCode.EN] ||
        ''
    )
  );

  const content =
    item.body?.richText?.[selectedLanguage] ||
    item.body?.richText?.[LanguageIsoCode.EN] ||
    '';

  const editorStateRichText = draftToHtml(
    convertToRaw(editorState.getCurrentContent())
  );

  useEffect(() => {
    if (content !== editorStateRichText) {
      const blocksFromHtml = htmlToDraft(content);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setEditorState(newEditorState);
    }
  }, [content, editorStateRichText]);

  const onEditorStateChangeHandler = (newEditorState) => {
    onBodyChange(index, newEditorState);
    setEditorState(newEditorState);
  };

  const titleContent = item.title?.content;

  const title = useMemo(() => {
    const translatedTitle = titleContent?.[selectedLanguage];

    if (typeof translatedTitle === 'string') {
      return translatedTitle;
    }

    return (
      titleContent?.[selectedLanguage] ||
      titleContent?.[LanguageIsoCode.EN] ||
      ''
    );
  }, [titleContent, selectedLanguage]);

  return (
    <DragItem
      key={item.id}
      dragId={String(item.id)}
      index={index}
      className={classes.drag}
    >
      <div className={classes['accordion-item']}>
        <div className={classes['item-header']}>
          <input
            type="text"
            className={classNames('input', classes['accordion-title'])}
            placeholder="Title"
            value={title}
            onChange={(e) => onTitleChange(index, e.target.value)}
          />
          <button
            className="button"
            type="button"
            onClick={() => onToggleOpen(index)}
          >
            {item.open && (
              <span className="icon">
                <i className="mdi mdi-minus" />
              </span>
            )}
            {!item.open && (
              <span className="icon">
                <i className="mdi mdi-plus" />
              </span>
            )}
          </button>
        </div>
        {item.open && (
          <div className={classes['item-body']}>
            <TextEditor
              textEditorState={{
                editorState,
              }}
              onEditorStateChange={onEditorStateChangeHandler}
            />
          </div>
        )}
      </div>
    </DragItem>
  );
};

export default AccordionItem;
